/**
 * Team — global, cross-workspace roster.
 *
 * Scope: surfaces every person who belongs to a workspace the caller owns or
 *        administers. Per-workspace mutation routes through the existing
 *        /workspaces/{id}/members/{member_id} endpoints (PUT for role,
 *        DELETE for remove) and /films/{id}/invitations/{invite_id} for
 *        cancelling pending film invitations.
 *
 * Tabs: Members | Pending Invites
 * Filters: free-text search, workspace, role, seat-type (billable/viewer).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../context/AuthContext';
import { API_BASE, BACKEND_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Users, Search, Mail, Crown, Loader2, Trash2, ExternalLink, ChevronDown,
  ShieldCheck, UserCircle2, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API = API_BASE;

const ROLE_LABELS = {
  owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer',
};
const ROLE_BADGE_CLASSES = {
  owner: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  admin: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  member: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  viewer: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20',
};
const ASSIGNABLE_ROLES = ['admin', 'member', 'viewer'];

const initialsOf = (name = '', email = '') => {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

const Avatar = ({ name, email, src, size = 'md' }) => {
  const dim = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-xs';
  // Backend stores avatar_url as a relative `/api/uploads/...` path.
  const url = src && src.startsWith('http') ? src : src ? `${BACKEND_URL}${src}` : null;
  if (url) {
    return (
      <img
        src={url}
        alt={name || email || ''}
        className={`${dim} rounded-full object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div className={`${dim} rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center flex-shrink-0`}>
      {initialsOf(name, email)}
    </div>
  );
};

/* ----------------------------- Workspace Chip ----------------------------- */

const WorkspaceChip = ({
  membership,
  onRoleChange,
  onRemove,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { workspace_id, workspace_name, role, is_owner, member_id } = membership;
  const protectedRow = is_owner || member_id === '__owner__';

  const handleRole = async (newRole) => {
    if (newRole === role) return;
    setBusy(true);
    try {
      await onRoleChange(membership, newRole);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await onRemove(membership);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-muted/60 ${ROLE_BADGE_CLASSES[role] || ROLE_BADGE_CLASSES.viewer}`}
          data-testid={`team-chip-${workspace_id}`}
        >
          {is_owner && <Crown className="h-3 w-3 text-amber-500" />}
          <span className="max-w-[140px] truncate">{workspace_name}</span>
          <span className="text-foreground/60">·</span>
          <span>{ROLE_LABELS[role] || role}</span>
          {!protectedRow && <ChevronDown className="h-3 w-3 opacity-60" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-3">
          <div>
            <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
              Workspace
            </p>
            <Link
              to={`/workspaces/${workspace_id}/members`}
              className="mt-1 flex items-center gap-1 text-sm font-medium hover:underline"
            >
              {workspace_name}
              <ExternalLink className="h-3 w-3 opacity-60" />
            </Link>
          </div>

          {protectedRow ? (
            <div className="rounded-md bg-amber-500/5 border border-amber-500/20 p-2 text-xs">
              <p className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400">
                <Crown className="h-3 w-3" /> Workspace owner
              </p>
              <p className="text-muted-foreground mt-1 leading-relaxed">
                Owner role can't be changed or removed from this view. Transfer ownership in workspace settings first.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                  Role
                </p>
                <Select value={role} onValueChange={handleRole} disabled={busy}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`team-chip-role-${workspace_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="text-xs">
                        {ROLE_LABELS[r]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-destructive hover:text-destructive"
                disabled={busy}
                onClick={handleRemove}
                data-testid={`team-chip-remove-${workspace_id}`}
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-2" />}
                Remove from workspace
              </Button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/* ------------------------------ Members Tab ------------------------------ */

const MembersTab = ({ members, loading, refresh, workspaces, currentUserId }) => {
  const [search, setSearch] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [seatFilter, setSeatFilter] = useState('all');

  const [pendingRemove, setPendingRemove] = useState(null); // { membership, person }

  const handleRoleChange = useCallback(async (membership, newRole) => {
    try {
      await axios.put(
        `${API}/workspaces/${membership.workspace_id}/members/${membership.member_id}`,
        { role: newRole },
        { headers: getAuthHeaders() },
      );
      toast.success(`Updated role in ${membership.workspace_name}`);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  }, [refresh]);

  const handleRemoveRequest = useCallback((membership, person) => {
    setPendingRemove({ membership, person });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!pendingRemove) return;
    const { membership } = pendingRemove;
    try {
      await axios.delete(
        `${API}/workspaces/${membership.workspace_id}/members/${membership.member_id}`,
        { headers: getAuthHeaders() },
      );
      toast.success(`Removed from ${membership.workspace_name}`);
      setPendingRemove(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  }, [pendingRemove, refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (members || []).filter((m) => {
      if (q) {
        const hay = `${m.name || ''} ${m.email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (workspaceFilter !== 'all') {
        if (!m.workspace_ids?.includes(workspaceFilter)) return false;
      }
      if (roleFilter !== 'all') {
        if (!m.roles?.includes(roleFilter)) return false;
      }
      if (seatFilter === 'billable' && !m.is_billable_anywhere) return false;
      if (seatFilter === 'viewer' && m.is_billable_anywhere) return false;
      return true;
    });
  }, [members, search, workspaceFilter, roleFilter, seatFilter]);

  const hasActiveFilter =
    search.trim() || workspaceFilter !== 'all' || roleFilter !== 'all' || seatFilter !== 'all';

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" data-testid="team-members-loading" />
      </div>
    );
  }

  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-9 h-9"
            data-testid="team-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
            <SelectTrigger className="h-9 w-[160px] text-xs" data-testid="team-filter-workspace">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All workspaces</SelectItem>
              {workspaces.map((w) => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="team-filter-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="viewer">Viewer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={seatFilter} onValueChange={setSeatFilter}>
            <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="team-filter-seat">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All seats</SelectItem>
              <SelectItem value="billable">Billable</SelectItem>
              <SelectItem value="viewer">Viewer (free)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Result count */}
      <p className="text-xs text-muted-foreground mb-3" data-testid="team-result-count">
        {filtered.length} {filtered.length === 1 ? 'person' : 'people'}
        {hasActiveFilter && members.length !== filtered.length && ` of ${members.length}`}
      </p>

      {/* Rows */}
      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <Users className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
          <p className="text-sm font-medium" data-testid="team-empty">
            {members.length === 0 ? 'No teammates yet' : 'No matches'}
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            {members.length === 0
              ? 'Invite people to a workspace and they\u2019ll show up here, across all your projects.'
              : 'Try clearing filters or searching for a different name.'}
          </p>
          {hasActiveFilter && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => { setSearch(''); setWorkspaceFilter('all'); setRoleFilter('all'); setSeatFilter('all'); }}
              data-testid="team-clear-filters"
            >
              <X className="h-3.5 w-3.5 mr-1.5" /> Clear filters
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-2" data-testid="team-members-list">
          {filtered.map((m) => {
            const isSelf = m.user_id === currentUserId;
            return (
              <Card key={m.user_id} className="p-4" data-testid={`team-row-${m.user_id}`}>
                <div className="flex items-start gap-3">
                  <Avatar name={m.name} email={m.email} src={m.avatar_url} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-sm truncate">{m.name || m.email || 'Unknown user'}</span>
                      {isSelf && <span className="text-[10px] text-muted-foreground">(you)</span>}
                      {m.is_billable_anywhere ? (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
                          <ShieldCheck className="h-2.5 w-2.5 mr-1" />Billable
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-zinc-500/30 text-zinc-600 dark:text-zinc-400">
                          Viewer
                        </Badge>
                      )}
                    </div>
                    {m.email && (
                      <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                    )}
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {m.memberships.map((mem) => (
                        <WorkspaceChip
                          key={`${m.user_id}-${mem.workspace_id}`}
                          membership={mem}
                          onRoleChange={handleRoleChange}
                          onRemove={(membership) => handleRemoveRequest(membership, m)}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Remove confirmation */}
      <AlertDialog open={!!pendingRemove} onOpenChange={(o) => { if (!o) setPendingRemove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemove && (
                <>
                  Remove <span className="font-semibold text-foreground">{pendingRemove.person?.name || pendingRemove.person?.email}</span>{' '}
                  from <span className="font-semibold text-foreground">{pendingRemove.membership?.workspace_name}</span>?
                  They will lose access to every film in that workspace. Memberships in other workspaces are preserved.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-remove-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="team-remove-confirm"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/* ----------------------------- Invitations Tab ----------------------------- */

const InvitationsTab = ({ invitations, loading, refresh, workspaces }) => {
  const [search, setSearch] = useState('');
  const [workspaceFilter, setWorkspaceFilter] = useState('all');
  const [pendingCancel, setPendingCancel] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (invitations || []).filter((inv) => {
      if (q) {
        const hay = `${inv.email || ''} ${inv.film_title || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (workspaceFilter !== 'all' && inv.workspace_id !== workspaceFilter) return false;
      return true;
    });
  }, [invitations, search, workspaceFilter]);

  const handleCancel = useCallback(async () => {
    if (!pendingCancel) return;
    try {
      await axios.delete(
        `${API}/films/${pendingCancel.film_id}/invitations/${pendingCancel.id}`,
        { headers: getAuthHeaders() },
      );
      toast.success('Invitation cancelled');
      setPendingCancel(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel invitation');
    }
  }, [pendingCancel, refresh]);

  if (loading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" data-testid="team-invites-loading" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or film…"
            className="pl-9 h-9"
            data-testid="team-invites-search"
          />
        </div>
        <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
          <SelectTrigger className="h-9 w-[180px] text-xs" data-testid="team-invites-workspace-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All workspaces</SelectItem>
            {workspaces.map((w) => (
              <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <p className="text-xs text-muted-foreground mb-3" data-testid="team-invites-count">
        {filtered.length} pending {filtered.length === 1 ? 'invitation' : 'invitations'}
      </p>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <Mail className="h-10 w-10 mx-auto text-muted-foreground/60 mb-3" />
          <p className="text-sm font-medium" data-testid="team-invites-empty">
            {invitations.length === 0 ? 'No pending invitations' : 'No matches'}
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            {invitations.length === 0
              ? 'Outgoing film invitations from your workspaces will appear here while they\u2019re pending.'
              : 'Try clearing filters to see all pending invites.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="team-invites-list">
          {filtered.map((inv) => (
            <Card key={inv.id} className="p-4 flex items-start gap-3" data-testid={`team-invite-${inv.id}`}>
              <div className="h-9 w-9 rounded-full bg-amber-500/10 text-amber-600 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{inv.email}</p>
                <p className="text-xs text-muted-foreground truncate">
                  Invited to <span className="text-foreground">{inv.film_title}</span> ·{' '}
                  <Link to={`/workspaces/${inv.workspace_id}`} className="hover:underline">{inv.workspace_name}</Link>
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5">
                    {ROLE_LABELS[inv.access_type] || inv.access_type}
                  </Badge>
                  <span>·</span>
                  <span>{inv.role}</span>
                  <span>·</span>
                  <span>Sent {new Date(inv.created_at).toLocaleDateString()}</span>
                  {inv.invited_by_name && <>
                    <span>·</span>
                    <span>by {inv.invited_by_name}</span>
                  </>}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setPendingCancel(inv)}
                data-testid={`team-invite-cancel-${inv.id}`}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!pendingCancel} onOpenChange={(o) => { if (!o) setPendingCancel(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel invitation?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCancel && (
                <>
                  Cancel the invitation to <span className="font-semibold text-foreground">{pendingCancel.email}</span> for{' '}
                  <span className="font-semibold text-foreground">{pendingCancel.film_title}</span>?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-invite-cancel-dismiss">Keep invitation</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="team-invite-cancel-confirm"
            >
              Cancel invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/* --------------------------------- Page --------------------------------- */

const Team = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('members');
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [loadingInvites, setLoadingInvites] = useState(true);
  const [accessChecked, setAccessChecked] = useState(false);

  const loadMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const res = await axios.get(`${API}/team/members`, { headers: getAuthHeaders() });
      setMembers(res.data || []);
    } catch (err) {
      toast.error('Failed to load team');
      setMembers([]);
    } finally {
      setLoadingMembers(false);
      setAccessChecked(true);
    }
  }, []);

  const loadInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      const res = await axios.get(`${API}/team/invitations`, { headers: getAuthHeaders() });
      setInvitations(res.data || []);
    } catch (err) {
      // Secondary tab — log for dev visibility but don't toast.
      console.error('Failed to load pending invitations:', err);
      setInvitations([]);
    } finally {
      setLoadingInvites(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
    loadInvites();
  }, [loadMembers, loadInvites]);

  // Derive the workspace list from members for the filter dropdown — only
  // workspaces I actually manage will appear via the /team/members payload.
  const managedWorkspaces = useMemo(() => {
    const map = new Map();
    (members || []).forEach((m) => {
      (m.memberships || []).forEach((mem) => {
        if (!map.has(mem.workspace_id)) {
          map.set(mem.workspace_id, { id: mem.workspace_id, name: mem.workspace_name });
        }
      });
    });
    (invitations || []).forEach((inv) => {
      if (!map.has(inv.workspace_id)) {
        map.set(inv.workspace_id, { id: inv.workspace_id, name: inv.workspace_name });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [members, invitations]);

  /* No managed workspaces → empty state encouraging upgrade / invite */
  const noManagedWorkspaces = accessChecked && managedWorkspaces.length === 0;

  return (
    <div className="bg-background" data-testid="team-page">
      <main className="container mx-auto px-4 md:px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-2xl md:text-3xl font-bold font-display tracking-tight">Team</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Everyone across the workspaces you own or administer — in one place.
          </p>
        </div>

        {noManagedWorkspaces ? (
          <Card className="p-12 text-center border-dashed">
            <UserCircle2 className="h-12 w-12 mx-auto text-muted-foreground/60 mb-4" />
            <h3 className="text-base font-semibold mb-1">No teams to manage yet</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto mb-5">
              The Team page surfaces members across every workspace you own or admin. Create or upgrade a workspace to start collaborating.
            </p>
            <div className="flex justify-center gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/workspaces" data-testid="team-empty-workspaces">View workspaces</Link>
              </Button>
              <Button asChild size="sm">
                <Link to="/workspace/purchase" data-testid="team-empty-upgrade">Upgrade plan</Link>
              </Button>
            </div>
          </Card>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-5" data-testid="team-tabs">
              <TabsTrigger value="members" data-testid="team-tab-members">
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Members
                {members.length > 0 && (
                  <span className="ml-1.5 text-[10px] text-muted-foreground">{members.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="invitations" data-testid="team-tab-invitations">
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                Pending Invites
                {invitations.length > 0 && (
                  <span className="ml-1.5 text-[10px] text-muted-foreground">{invitations.length}</span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="members" className="mt-0">
              <MembersTab
                members={members}
                loading={loadingMembers}
                refresh={loadMembers}
                workspaces={managedWorkspaces}
                currentUserId={user?.id}
              />
            </TabsContent>

            <TabsContent value="invitations" className="mt-0">
              <InvitationsTab
                invitations={invitations}
                loading={loadingInvites}
                refresh={loadInvites}
                workspaces={managedWorkspaces}
              />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default Team;
